// Single Blog Post Page - SEO Optimized with Schema
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Layout } from '../components/Layout';
import { Calendar, Eye, User, ArrowLeft, Share2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBlogPost();
  }, [slug]);

  async function loadBlogPost() {
    try {
      const response = await api.request(`/blog/posts/${slug}`);
      setPost(response);
    } catch (err) {
      console.error('Failed to load blog post:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function sharePost() {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-600">Loading article...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">Article not found</p>
            <Link to="/blog">
              <Button>← Back to Blog</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const schemaOrgArticle = {
    "@context": "https://schema.org",
    "@type": post.schema_type || "BlogPosting",
    "headline": post.title,
    "description": post.meta_description,
    "image": post.featured_image_url || "https://upost.com/default-blog.jpg",
    "datePublished": post.published_at,
    "dateModified": post.updated_at,
    "author": {
      "@type": "Organization",
      "name": post.author_name
    },
    "publisher": {
      "@type": "Organization",
      "name": "UPost",
      "logo": {
        "@type": "ImageObject",
        "url": "https://upost.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://upost.com/blog/${post.slug}`
    },
    "keywords": post.keywords?.join(', ')
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {"@type":"ListItem","position":1,"name":"Home","item":"https://upost.com"},
      {"@type":"ListItem","position":2,"name":"Blog","item":"https://upost.com/blog"},
      {"@type":"ListItem","position":3,"name":post.title,"item":`https://upost.com/blog/${post.slug}`}
    ]
  };

  return (
    <Layout>
      <SeoHead
        title={`${post.title} | UPost Blog`}
        description={post.meta_description}
        keywords={post.keywords?.join(', ')}
        canonical={`https://upost.com/blog/${post.slug}`}
        og={{
          type: 'article',
          url: `https://upost.com/blog/${post.slug}`,
          title: post.title,
          description: post.meta_description,
          image: post.featured_image_url || 'https://upost.com/mark-twain-og.jpg',
          siteName: 'UPost'
        }}
        twitter={{
          card: 'summary_large_image',
          title: post.title,
          description: post.meta_description,
          image: post.featured_image_url || 'https://upost.com/mark-twain-og.jpg'
        }}
        schemas={[schemaOrgArticle, breadcrumbSchema]}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Article */}
        <article className="py-12 px-4">
          <div className="container mx-auto max-w-4xl">
            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-6">
              <Link to="/" className="hover:text-purple-600">Home</Link>
              {' > '}
              <Link to="/blog" className="hover:text-purple-600">Blog</Link>
              {' > '}
              <span className="text-gray-900">{post.title}</span>
            </nav>

            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {post.title}
              </h1>
              
              <div className="flex items-center gap-6 text-sm text-gray-600 mb-6">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {post.author_name}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.published_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                {post.view_count > 0 && (
                  <span className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    {post.view_count.toLocaleString()} views
                  </span>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={sharePost}
                  className="ml-auto"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>

              {/* Keywords */}
              {post.keywords && post.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {post.keywords.map((keyword, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {/* Featured Image */}
            {post.featured_image_url && (
              <img 
                src={post.featured_image_url} 
                alt={post.title}
                className="w-full h-auto rounded-xl shadow-lg mb-8"
              />
            )}

            {/* Article Content */}
            <Card className="mb-8">
              <CardContent className="prose prose-lg max-w-none p-8">
                <ReactMarkdown
                  components={{
                    h2: ({node, ...props}) => <h2 className="text-3xl font-bold mt-8 mb-4" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-2xl font-bold mt-6 mb-3" {...props} />,
                    p: ({node, ...props}) => <p className="mb-4 leading-relaxed text-gray-700" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                    li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                    a: ({node, ...props}) => <a className="text-purple-600 hover:text-purple-700 underline" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                    blockquote: ({node, ...props}) => (
                      <blockquote className="border-l-4 border-purple-600 pl-4 italic my-4 text-gray-600" {...props} />
                    ),
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Write Your Book?</h3>
                <p className="text-purple-100 mb-6 text-lg">
                  Use UPost's AI-powered tools to write 10x faster and publish successfully
                </p>
                <Link to="/register">
                  <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6">
                    Start Writing Free - No Card Required
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Authors' Exchange Cross-Post CTA */}
            <Card className="border-amber-500/30 bg-amber-50 mt-6" data-testid="blog-club-cta">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Get Free Books & More Amazon Reviews</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Join The Authors' Exchange — claim free advance reader copies, review books to earn credits, and launch your own book with a team of reviewers ready on day one.
                </p>
                <Link to="/club">
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                    Join Free — Get 50 Credits
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </article>
      </div>
    </Layout>
  );
}
